/*
 * MEZUZAH COMPLETE YOUR MEZUZAH ADDONS v2 (WO-373, 2026-09-08)
 * Own-code, no shadow DOM. Vanilla, deferred, one listener setup per
 * .mz-cym block on the page (realistically one, per product template).
 *
 * Two behaviors:
 * 1. Live summary line + running total as scroll toggles change, and
 *    a matching update to the native add-to-cart button's own
 *    [data-product-price] element (WO-372's own finding: theme.dev.js's
 *    ProductForm.toggleSubmitButton() always re-reads that element's
 *    live outerHTML rather than a cached value, so writing into it
 *    directly here is safe and survives later button-text swaps).
 * 2. On submit of the product form this block belongs to, when at
 *    least one scroll is toggled: build a single multi-item /cart/add.js
 *    request (case + each toggled scroll, each scroll carrying
 *    properties["Scroll Preparation"]), and hand it to the theme's own
 *    <cart-items> custom element's addToCart(items, button) method so
 *    the existing getCart()/build()/"theme:product:added" drawer-open
 *    chain (assets/theme.dev.js) runs unmodified -- this file does not
 *    fork or duplicate that logic. ASSUMPTION, not directly confirmed
 *    in source (Broadcast's production build is minified): the custom
 *    element is registered under the tag name "cart-items", following
 *    the same convention Shopify's Dawn reference theme uses for this
 *    exact class shape. If that tag isn't found on the page, a raw
 *    fetch + full page reload is the fallback, so the add itself is
 *    always correct even if the tag name assumption is wrong -- only
 *    the animated-drawer polish would be lost. Flagged as an open
 *    question in docs/wo/WO-373-console.md for QA to confirm.
 */
(function () {
  'use strict';

  function moneyFormatter(currency, locale) {
    try {
      var fmt = new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: currency || 'USD' });
      return function (cents) {
        return fmt.format(cents / 100);
      };
    } catch (e) {
      return function (cents) {
        return '$' + (cents / 100).toFixed(2);
      };
    }
  }

  function collectFormProperties(form) {
    var props = {};
    /*
     * WO-654 F06: form.elements, not form.querySelectorAll. Property inputs that belong to this form through the form=
     * attribute but sit outside it in the DOM (the pre-order property in snippets/product.liquid, the ones in
     * snippets/product-properties.liquid) are only found through form.elements; the old query dropped them from the
     * case line whenever a scroll was ticked. Broadcast's own add path reads new FormData(form), which includes them.
     */
    var fields = Array.prototype.slice.call(form.elements || form.querySelectorAll('[name^="properties["]')).filter(function (field) {
      return field.name && field.name.indexOf('properties[') === 0;
    });
    fields.forEach(function (field) {
      var match = field.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      var key = match[1];
      if (field.type === 'checkbox' || field.type === 'radio') {
        if (field.checked && field.value) props[key] = field.value;
      } else if (field.value) {
        props[key] = field.value;
      }
    });
    return props;
  }

  function initBlock(block) {
    var form = document.getElementById(block.dataset.productFormId);
    if (!form) return;

    var format = moneyFormatter(block.dataset.currency, block.dataset.locale);
    var summaryEl = block.querySelector('[data-mz-cym-summary]');
    var labelEl = summaryEl ? summaryEl.querySelector('.mz-cym-summary__label') : null;
    var totalEl = summaryEl ? summaryEl.querySelector('.mz-cym-summary__total') : null;
    var prepInput = block.querySelector('[data-mz-cym-prep-input]');
    var priceEl = document.querySelector('[data-add-to-cart] [data-product-price]');
    var initialPriceHTML = priceEl ? priceEl.innerHTML : null;
    var basePriceCents = parseInt(summaryEl ? summaryEl.dataset.basePriceCents : '0', 10) || 0;

    function toggles() {
      return Array.prototype.slice.call(block.querySelectorAll('[data-mz-cym-toggle]'));
    }

    /*
     * WO-719 BUY-04 (Codex): ONE quantity-aware calculation for the summary, the button price and the cart payload. The quantity field is read
     * here, the case goes in caseQty times and each ticked scroll follows it unless the block setting "Add-on quantity follows the case" is off
     * (then 1), so a total shown can never differ from what is added.
     */
    function quantities() {
      var qtyField = form.querySelector('[name="quantity"]');
      var caseQty = qtyField && qtyField.value ? parseInt(qtyField.value, 10) : 1;
      if (!(caseQty >= 1)) caseQty = 1;
      return { caseQty: caseQty, addonQty: block.dataset.mzCymQtyFollows === 'false' ? 1 : caseQty };
    }

    function updateSummary() {
      var checked = toggles().filter(function (t) {
        return t.checked;
      });
      var extraCents = checked.reduce(function (sum, t) {
        return sum + (parseInt(t.dataset.priceCents, 10) || 0);
      }, 0);
      var qty = quantities();
      var totalCents = basePriceCents * qty.caseQty + extraCents * qty.addonQty;
      var count = checked.length;

      if (summaryEl && labelEl && totalEl) {
        var prefix = summaryEl.dataset.labelPrefix || 'Your mezuzah';
        var line;
        if (count === 0) {
          line = summaryEl.dataset.labelCaseOnly || 'Case only';
        } else {
          line = (summaryEl.dataset.labelWithScrolls || 'Case + {count} scroll(s)')
            .replace('{count}', count)
            .replace('scroll(s)', count === 1 ? 'scroll' : 'scrolls');
        }
        labelEl.textContent = prefix + ': ' + line;
        totalEl.textContent = format(totalCents);
      }

      if (priceEl) {
        if (count === 0 && initialPriceHTML !== null) {
          priceEl.innerHTML = initialPriceHTML;
        } else {
          priceEl.innerHTML = format(totalCents);
        }
      }

      document.dispatchEvent(
        new CustomEvent('mz:cym:total', { detail: { cents: totalCents, count: count, quantity: qty.caseQty } })
      );
    }

    // the quantity field changes the total too (typed, or the +/- buttons)
    ['input', 'change'].forEach(function (type) {
      form.addEventListener(type, function (evt) { if (evt.target && evt.target.matches && evt.target.matches('[name="quantity"]')) updateSummary(); });
    });
    form.addEventListener('click', function (evt) { if (evt.target && evt.target.closest && evt.target.closest('[data-quantity-selector], .quantity, [data-quantity-minus], [data-quantity-plus]')) setTimeout(updateSummary, 0); });

    block.addEventListener('change', function (evt) {
      if (evt.target.matches('[data-mz-cym-toggle]')) updateSummary();
    });

    /*
     * V20 polish (WO-397, WO-394 A.6): when the theme's add reports no
     * success and its own error container stays empty, say so instead of
     * silently re-enabling the button.
     */
    var errorEl = block.querySelector('[data-mz-cym-error]');
    function showError(show) {
      if (!errorEl) return;
      errorEl.classList.toggle('is-visible', !!show);
    }

    /*
     * V21 (B-79, QA WO-401 step 3, 2026-09-09): Broadcast writes the raw
     * exception into the product form's error box on a network drop or a
     * cart error without a description ("Failed to fetch: undefined",
     * "Cart Error:"), next to its friendly drawer message. Exactly one
     * customer-facing message is the rule: technical text in the form box is
     * removed; if the drawer shows nothing either, our own message shows.
     */
    var TECHNICAL = /failed to fetch|failed to execute|unexpected end of json|networkerror|load failed|typeerror|: undefined\s*$|^cart error:?\s*$|^error:?\s*$/i;
    function formErrorBoxes() {
      var scope = form.closest('[data-section-type], .product__page, section') || document;
      return Array.prototype.slice.call(scope.querySelectorAll('[data-cart-errors-container]'));
    }
    function drawerErrorText() {
      var el = document.querySelector('[data-cart-errors], .cart__errors');
      return el ? el.textContent.trim() : '';
    }
    function tidyThemeErrors() {
      var friendlyShown = drawerErrorText().length > 0;
      formErrorBoxes().forEach(function (box) {
        var text = box.textContent.trim();
        if (!text) return;
        if (TECHNICAL.test(text)) {
          box.textContent = '';
          box.classList.remove('is-visible');
          return;
        }
        friendlyShown = true; // a real message from the theme stands
      });
      return friendlyShown;
    }

    var buttonsBlock = form.closest('.product__block--buttons');
    var productInfoRoot = block.closest('product-info') || document;
    var shippingInfoEl = productInfoRoot.querySelector('.mz-shipping-info');

    /*
     * WO-417 (Shay 2026-09-10, version C): a sold-out case hides the scroll
     * toggles (Section Store threw an error there) and keeps each fitting
     * scroll reachable as a plain link; the buy column's quantity box
     * hides and its Sold Out button and Klaviyo's Notify Me button
     * restyle via CSS keyed off mz-cym--soldout / mz-soldout-active
     * (assets/mezuzah-cym-v2.css). WO-419 amendment (Shay 01:42 UTC) adds
     * the Ships by row (mz-shipping-info--soldout). All classes are set
     * together here so the whole buy column flips state at once.
     */
    var initialising = true;

    function setSoldOut(on) {
      block.classList.toggle('mz-cym--soldout', on);
      if (buttonsBlock) buttonsBlock.classList.toggle('mz-soldout-active', on);
      if (shippingInfoEl) shippingInfoEl.classList.toggle('mz-shipping-info--soldout', on);

      toggles().forEach(function (t) {
        t.disabled = on;
        if (on && t.checked) t.checked = false;
      });

      /*
       * WO-436 (DESIGN WO-432 finding 1, 2026-09-10): a closed <details>
       * hides its content whatever the CSS says, so while sold out every
       * size-group expander is forced open (its summary is hidden by the
       * sold-out CSS) and the previous state is restored when the variant
       * becomes available. The Liquid renders the open attribute for the
       * first paint; a Liquid-rendered open state counts as "was closed".
       */
      var expanders = block.querySelectorAll('.mz-cym__expander');
      Array.prototype.forEach.call(expanders, function (d) {
        if (on) {
          if (!d.hasAttribute('data-mz-prev-open')) {
            d.setAttribute('data-mz-prev-open', d.open && !initialising ? '1' : '0');
          }
          d.open = true;
        } else if (d.hasAttribute('data-mz-prev-open')) {
          d.open = d.getAttribute('data-mz-prev-open') === '1';
          d.removeAttribute('data-mz-prev-open');
        }
      });
      updateSummary();
    }

    /*
     * Variant-change hook. Broadcast does NOT dispatch a document-level
     * "theme:variant:change" event with a detail.variant payload -- checked
     * the shipped assets/theme.dev.js and assets/product-info.js, neither
     * exists. Confirmed instead: <product-info> (snippets/product.liquid,
     * wraps the whole buy column including this block) dispatches
     * "theme:product-info:loaded" (bubbles: true) once it has swapped in
     * updated variant markup, and separately toggles its own
     * "variant--soldout" class on the product-form wrapper. Availability
     * is read here from the add-to-cart button's own disabled attribute
     * (snippets/product-buttons.liquid sets it from the identical
     * current_variant.available/quantity_rule_soldout condition this
     * block's own Liquid uses), which is already correct by the time this
     * event fires. A MutationObserver on that same attribute, plus a
     * change listener on the form's own [name="id"] input, is kept as the
     * WO's own requested fallback in case a template ever renders this
     * block without a <product-info> ancestor.
     */
    function checkAvailability() {
      var btn = form.querySelector('[data-add-to-cart]');
      if (!btn) return;
      setSoldOut(btn.disabled);
    }

    document.addEventListener('theme:product-info:loaded', checkAvailability);

    var idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.addEventListener('change', checkAvailability);

    var soldoutObserverTarget = form.querySelector('[data-add-to-cart]');
    if (soldoutObserverTarget && window.MutationObserver) {
      new MutationObserver(checkAvailability).observe(soldoutObserverTarget, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    setSoldOut(block.classList.contains('mz-cym--soldout'));
    initialising = false;

    /*
     * Row click while sold out: rows have no toggle to activate, so a
     * click anywhere in the row's label (not already on the title anchor,
     * which navigates on its own) behaves like clicking the title.
     * Modifier clicks (middle button, ctrl/cmd) keep the browser's own
     * open-in-new-tab behaviour.
     */
    block.addEventListener('click', function (evt) {
      if (!block.classList.contains('mz-cym--soldout')) return;
      if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      if (evt.target.closest('.mz-cym-row__title')) return; // anchor already navigates
      var label = evt.target.closest('.mz-cym-row__label');
      if (!label) return;
      var link = label.querySelector('.mz-cym-row__title');
      if (!link) return;
      evt.preventDefault();
      window.location.href = link.href;
    });

    updateSummary();

    /*
     * CTO review fix (WO-373, 2026-09-08 20:05 UTC): Broadcast's own
     * <product-form> element registers `this.form.addEventListener('submit',
     * onSubmitHandler)` in its constructor (confirmed in the shipped
     * theme.js), so a second listener on the same form runs AFTER the
     * theme has already started its single-item add: the case would be
     * added twice. Listening in the CAPTURE phase on document runs before
     * any listener on the form itself; when at least one scroll is toggled
     * we take over the submit completely (preventDefault +
     * stopImmediatePropagation) and hand the multi-item add to
     * <cart-items>.addToCart(). With no scroll toggled we do nothing and the
     * theme's native single-item flow runs untouched.
     */
    document.addEventListener('submit', function (evt) {
      if (evt.target !== form) return;
      if (block.classList.contains('mz-cym--soldout')) return; // native sold-out flow, untouched
      var checked = toggles().filter(function (t) {
        return t.checked;
      });
      if (checked.length === 0) return; // native single-item flow, untouched

      evt.preventDefault();
      evt.stopImmediatePropagation();

      var idField = form.querySelector('[name="id"]');
      if (!idField || !idField.value) return;

      var prepValue = prepInput ? prepInput.value : '';
      var caseProperties = collectFormProperties(form);

      /*
       * Pair quantity (ChatGPT site audit, Sep 30, 2026): every ticked scroll used to go in with quantity 1, so a
       * quantity of 8 plus one scroll gave 8 cases and 1 scroll. The quantity field is read once here, at submit,
       * so a change after ticking is picked up; each ticked add-on follows it while the block setting
       * "Add-on quantity follows the case" (data-mz-cym-qty-follows) is on. Off keeps the old quantity of 1.
       */
      var qty = quantities();
      var caseQty = qty.caseQty;
      var addonQty = qty.addonQty;

      var items = [
        {
          id: idField.value,
          quantity: caseQty,
          properties: caseProperties,
        },
      ];
      checked.forEach(function (t) {
        items.push({
          id: t.dataset.variantId,
          quantity: addonQty,
          properties: { 'Scroll Preparation': prepValue },
        });
      });

      var submitButton = form.querySelector('[data-add-to-cart]');
      var cartItemsEl = document.querySelector('cart-items');

      showError(false);

      if (cartItemsEl && typeof cartItemsEl.addToCart === 'function') {
        var succeeded = false;
        var onAdded = function () { succeeded = true; };
        if (submitButton) submitButton.addEventListener('theme:product:add', onAdded, { once: true });
        cartItemsEl.addToCart(items, submitButton);
        var startedAt = Date.now();
        var watch = setInterval(function () {
          var elapsed = Date.now() - startedAt;
          if (succeeded) { clearInterval(watch); return; }
          var themeErrorShown = tidyThemeErrors();
          if (submitButton && !submitButton.disabled && elapsed > 1500) {
            clearInterval(watch);
            if (!themeErrorShown) showError(true);
            // Broadcast can write its form error a beat after re-enabling the button.
            setTimeout(function () {
              if (!tidyThemeErrors() && !succeeded) showError(true);
            }, 800);
            return;
          }
          if (elapsed > 20000) clearInterval(watch);
        }, 250);
        return;
      }

      var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart add failed');
          window.location.href = window.location.pathname + window.location.search;
        })
        .catch(function () {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('is-loading', 'loading');
          }
          showError(true);
        });
    }, true);
  }

  function init() {
    document.querySelectorAll('[data-mz-cym]').forEach(initBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
